import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';
import { ArrowUp, ArrowDown } from 'lucide-react';

// =============================================================================
// KpiBar / KpiCard / DeltaChip — v2 KPI-strip primitives.
//
// Module exports:
//   · KpiBar (default)    — container: 3-5 column responsive grid
//   · KpiCard (named)     — basic card: icon tile + mono eyebrow + value +
//                           optional suffix + optional delta chip
//   · DeltaChip (named)   — +/-percent indicator with direction arrow
//
// Composition pattern:
//
//   <KpiBar>
//     <KpiCard icon={<ShoppingCart/>} iconTint="primary" label="Orders"
//              value="1,234" />
//     <KpiCard ... delta={{ value: 12.5, prev: 1097, ranges: '...' }} />
//     <FlightDatesCard ... />   {/* custom card — any JSX with
//                                     .campaign-overview__kpi class */}
//   </KpiBar>
//
// KpiBar takes `children` (not `items[]`) so consumers can mix `<KpiCard>`
// with fully-custom cards (e.g. FlightDates with a progress bar, or a
// date-range picker KPI). Six Campaign Details consumers had duplicate
// local `KpiCard` definitions before this primitive — they all collapse
// to a single import now.
//
// Uses the `.campaign-overview__kpi-*` BEM scope from `_overview.scss` for
// now; a follow-up promotes those classes to a domain-agnostic `.kpi-*`
// scope in `_design-system.scss`. Until then, consumers outside Campaign
// Details need to import `_overview.scss` or redefine the classes.
// =============================================================================

export const DeltaChip = ({ value, prev, ranges }) => {
  if (value === undefined || value === null) return null;
  const num = Number(value);
  if (!isFinite(num) || num === 0) return null;

  const direction = num > 0 ? 'up' : 'down';
  const displayValue = `${num > 0 ? '+' : '-'}${Math.abs(num).toFixed(1)}%`;

  return (
    <Tooltip
      overlayClassName="mjl-ds"
      title={
        <>
          {prev !== undefined && prev !== null && <div>Previous: {prev}</div>}
          {ranges && <div>Range: {ranges}</div>}
        </>
      }
    >
      <span className={`delta-chip delta-chip--${direction}`}>
        {direction === 'up' ? (
          <ArrowUp size={10} strokeWidth={2.5} />
        ) : (
          <ArrowDown size={10} strokeWidth={2.5} />
        )}
        {displayValue}
      </span>
    </Tooltip>
  );
};

DeltaChip.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  prev: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ranges: PropTypes.string,
};

export const KpiCard = ({
  icon,
  iconTint = 'primary',
  label,
  labelTooltip,
  trailing,
  value,
  valueSuffix,
  delta,
  children,
  footer,
  className,
}) => {
  const extraClass = className ? ` ${className}` : '';
  // `children` overrides the `value`/`valueSuffix` slot when the card body
  // needs arbitrary JSX (e.g. DetailBar4Col's flight-dates card with a
  // progress bar, or a composite number-plus-label layout).
  const body =
    children !== undefined ? (
      children
    ) : (
      <>
        {value}
        {valueSuffix && <small>{valueSuffix}</small>}
      </>
    );
  // When `trailing` is passed the head needs a `__kpi-head-left` wrapper so
  // icon + eyebrow collapse to a single left-side unit and the trailing
  // slot distributes to the right via `justify-content: space-between`.
  const eyebrow = labelTooltip ? (
    <Tooltip overlayClassName="mjl-ds" title={labelTooltip}>
      <span className="campaign-overview__kpi-eyebrow">{label}</span>
    </Tooltip>
  ) : (
    <span className="campaign-overview__kpi-eyebrow">{label}</span>
  );
  const head = trailing ? (
    <div className="campaign-overview__kpi-head">
      <div className="campaign-overview__kpi-head-left">
        <span
          className={`campaign-overview__kpi-icon campaign-overview__kpi-icon--${iconTint}`}
        >
          {icon}
        </span>
        {eyebrow}
      </div>
      {trailing}
    </div>
  ) : (
    <div className="campaign-overview__kpi-head">
      <span
        className={`campaign-overview__kpi-icon campaign-overview__kpi-icon--${iconTint}`}
      >
        {icon}
      </span>
      {eyebrow}
    </div>
  );
  return (
    <div className={`campaign-overview__kpi${extraClass}`}>
      {head}
      <div className="campaign-overview__kpi-value">{body}</div>
      <div className="campaign-overview__kpi-footer">
        {delta ? <DeltaChip {...delta} /> : null}
      </div>
      {footer}
    </div>
  );
};

KpiCard.propTypes = {
  icon: PropTypes.node,
  iconTint: PropTypes.oneOf([
    'primary',
    'emerald',
    'amber',
    'critical',
    'indigo',
    'purple',
    'sky',
    'slate',
    'neutral',
  ]),
  label: PropTypes.node.isRequired,
  labelTooltip: PropTypes.node,
  trailing: PropTypes.node,
  value: PropTypes.node,
  valueSuffix: PropTypes.node,
  delta: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    prev: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ranges: PropTypes.string,
  }),
  children: PropTypes.node,
  footer: PropTypes.node,
  className: PropTypes.string,
};

const KpiBar = ({ children, className }) => {
  const extraClass = className ? ` ${className}` : '';
  return <div className={`campaign-overview__kpi-bar${extraClass}`}>{children}</div>;
};

KpiBar.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

export default KpiBar;
